import {
  ERROR_DARK,
  ERROR_LIGHT,
  ERROR_MAIN,
  INFO_DARK,
  INFO_LIGHT,
  INFO_MAIN,
  SUCCESS_DARK,
  SUCCESS_LIGHT,
  SUCCESS_MAIN,
  WARNING_DARK,
  WARNING_LIGHT,
  WARNING_MAIN
} from "./variables";
import { Theme } from "@mui/material";
import { ComponentsProps } from "@mui/material/styles/props";
import { ComponentsOverrides } from "@mui/material/styles/overrides";
import { ComponentsVariants } from "@mui/material/styles/variants";

export const generateDefaultGJTypography = (font: string) => ({
  fontFamily: font,
  h1: {
    fontStyle: "normal",
    fontWeight: "700",
    fontFamily: font,
    fontSize: "32px",
    lineHeight: "38px",
    letterSpacing: "normal"
  },
  h2: {
    fontStyle: "normal",
    fontWeight: "700",
    fontFamily: font,
    fontSize: "24px",
    lineHeight: "28px",
    letterSpacing: "normal"
  },
  h3: {
    fontStyle: "normal",
    fontWeight: "700",
    fontFamily: font,
    fontSize: "20px",
    lineHeight: "24px",
    letterSpacing: "normal"
  },
  h4: {
    fontStyle: "normal",
    fontWeight: "700",
    fontFamily: font,
    fontSize: "16px",
    lineHeight: "20px",
    letterSpacing: "normal"
  },
  subtitle1: {
    fontStyle: "normal",
    fontWeight: "400",
    fontFamily: font,
    fontSize: "24px",
    lineHeight: "28px",
    letterSpacing: "normal"
  },
  body1: {
    fontFamily: font,
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "16px",
    lineHeight: "20px"
  },
  body2: {
    fontFamily: font,
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "12px",
    lineHeight: "14px"
  },
  caption: {
    fontFamily: font,
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "12px",
    lineHeight: "16px"
  }
});
export const generateErrorWarnInfoColors = () => ({
  success: {
    main: SUCCESS_MAIN,
    light: SUCCESS_LIGHT,
    dark: SUCCESS_DARK
  },
  error: {
    main: ERROR_MAIN,
    light: ERROR_LIGHT,
    dark: ERROR_DARK
  },
  info: {
    main: INFO_MAIN,
    light: INFO_LIGHT,
    dark: INFO_DARK
  },
  warning: {
    main: WARNING_MAIN,
    light: WARNING_LIGHT,
    dark: WARNING_DARK
  }
});

export const styleRootCommonAppBar = ({ theme }: { theme: Theme }) => ({
  [theme.breakpoints.up("md")]: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.primary.main}`
  }
});

export const commonBorderRadius = 8;
export const zeroBorderRadius = 0;

export const MuiChip: {
  defaultProps?: ComponentsProps["MuiChip"];
  styleOverrides?: ComponentsOverrides<Theme>["MuiChip"];
  variants?: ComponentsVariants["MuiChip"];
} = {
  variants: [
    {
      props: { color: "primary" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        "& .MuiChip-icon": {
          color: theme.palette.primary.main
        },
        "& .MuiChip-deleteIcon": {
          color: theme.palette.primary.main
        },
        "&.MuiChip-outlined": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.text.primary
        }
      })
    },
    {
      props: { color: "error" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
        "& .MuiChip-icon": {
          color: theme.palette.error.dark
        },
        "& .MuiChip-deleteIcon": {
          color: theme.palette.error.dark
        },
        "&.MuiChip-outlined": {
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.error.dark
        }
      })
    },
    {
      props: { color: "warning" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        "& .MuiChip-icon": {
          color: theme.palette.warning.dark
        },
        "& .MuiChip-deleteIcon": {
          color: theme.palette.warning.dark
        },
        "&.MuiChip-outlined": {
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.warning.dark
        }
      })
    },
    {
      props: { color: "info" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
        "& .MuiChip-icon": {
          color: theme.palette.info.dark
        },
        "& .MuiChip-deleteIcon": {
          color: theme.palette.info.dark
        },
        "&.MuiChip-outlined": {
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.info.dark
        }
      })
    },
    {
      props: { color: "success" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
        "& .MuiChip-icon": {
          color: theme.palette.success.dark
        },
        "& .MuiChip-deleteIcon": {
          color: theme.palette.success.dark
        },
        "&.MuiChip-outlined": {
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.success.dark
        }
      })
    },
    {
      props: { size: "small" },
      style: ({ theme }) => ({
        fontSize: theme.spacing(1.5),
        padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`
      })
    },
    {
      props: { size: "medium" },
      style: ({ theme }) => ({
        fontSize: theme.spacing(1.75),
        padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
        height: theme.spacing(3.5)
      })
    },
    {
      props: { size: "large" },
      style: ({ theme }) => ({
        fontSize: theme.spacing(1.75),
        padding: `${theme.spacing(0.5)} ${theme.spacing(1.25)}`
      })
    }
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.spacing(0.75),
      "& .MuiChip-label": {
        padding: 0
      },
      "& .MuiChip-icon": {
        width: theme.spacing(2),
        height: theme.spacing(2),
        marginLeft: 0,
        marginRight: theme.spacing(0.5)
      },
      "& .MuiChip-deleteIcon": {
        marginRight: 0,
        marginLeft: theme.spacing(0.5)
      }
    })
  }
};

/**
 * Default GJ button style
 */
export const MuiButton: {
  defaultProps?: ComponentsProps["MuiButton"];
  styleOverrides?: ComponentsOverrides<Theme>["MuiButton"];
  variants?: ComponentsVariants["MuiButton"];
} = {
  variants: [
    {
      props: {},
      style: ({ theme }) => ({
        textTransform: "none",
        "&:disabled": {
          color: theme.palette.grey[500],
          backgroundColor: theme.palette.grey[300],
          borderColor: theme.palette.grey[300]
        },
        "&:hover": {
          boxShadow: "0px 6px 10px -4px rgba(0, 0, 0, 0.3)",
          "@media (hover: none)": {
            boxShadow: "0px 6px 10px -4px rgba(0, 0, 0, 0.3)"
          }
        }
      })
    },

    // VARIANT PRIMARY
    {
      props: {
        variant: "primary"
      },
      style: ({ theme }) => ({
        color: theme.palette.common.white
      })
    },
    {
      props: {
        variant: "primary",
        color: "primary"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.common.black,
        "&:hover": {
          backgroundColor: theme.palette.grey[800],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[800]
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "neutral"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.grey[600],
        "&:hover": {
          backgroundColor: theme.palette.grey[500],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[500]
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "success"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.success.main,
        "&:hover": {
          backgroundColor: theme.palette.success.dark,
          "@media (hover: none)": {
            backgroundColor: theme.palette.success.dark
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "error"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.error.main,
        "&:hover": {
          backgroundColor: theme.palette.error.dark,
          "@media (hover: none)": {
            backgroundColor: theme.palette.error.dark
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "warning"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.warning.main,
        "&:hover": {
          backgroundColor: theme.palette.warning.dark,
          "@media (hover: none)": {
            backgroundColor: theme.palette.warning.dark
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "info"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.info.main,
        "&:hover": {
          backgroundColor: theme.palette.info.dark,
          "@media (hover: none)": {
            backgroundColor: theme.palette.info.dark
          }
        }
      })
    },
    {
      props: {
        variant: "primary",
        color: "accent"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.accent.main,
        "&:hover": {
          backgroundColor: theme.palette.accent.dark,
          "@media (hover: none)": {
            backgroundColor: theme.palette.accent.dark
          }
        }
      })
    },

    // VARIANT SECONDARY
    {
      props: {
        variant: "secondary"
      },
      style: () => ({
        border: `1px solid`,
        backgroundColor: "unset",
        "&:disabled": {
          backgroundColor: "unset"
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "primary"
      },
      style: ({ theme }) => ({
        color: theme.palette.common.black,
        borderColor: theme.palette.common.black,
        "&:hover": {
          backgroundColor: theme.palette.grey[200],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[200]
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "neutral"
      },
      style: ({ theme }) => ({
        color: theme.palette.grey[700],
        borderColor: theme.palette.grey[500],
        "&:hover": {
          backgroundColor: theme.palette.grey[200],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[200]
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "success"
      },
      style: ({ theme }) => ({
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        "&:hover": {
          backgroundColor: theme.palette.success.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.success.light
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "error"
      },
      style: ({ theme }) => ({
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        "&:hover": {
          backgroundColor: theme.palette.error.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.error.light
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "warning"
      },
      style: ({ theme }) => ({
        color: theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
        "&:hover": {
          backgroundColor: theme.palette.warning.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.warning.light
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "info"
      },
      style: ({ theme }) => ({
        color: theme.palette.info.main,
        borderColor: theme.palette.info.main,
        "&:hover": {
          backgroundColor: theme.palette.info.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.info.light
          }
        }
      })
    },
    {
      props: {
        variant: "secondary",
        color: "accent"
      },
      style: ({ theme }) => ({
        color: theme.palette.accent.main,
        borderColor: theme.palette.accent.main,
        "&:hover": {
          backgroundColor: theme.palette.accent.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.accent.light
          }
        }
      })
    },

    // VARIANT GHOST
    {
      props: {
        variant: "ghost"
      },
      style: () => ({
        border: "none",
        backgroundColor: "unset",
        "&:disabled": {
          backgroundColor: "unset"
        },
        "&:hover": {
          boxShadow: "none",
          "@media (hover: none)": {
            boxShadow: "none"
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "primary"
      },
      style: ({ theme }) => ({
        color: theme.palette.common.black,
        "&:hover": {
          backgroundColor: theme.palette.grey[300],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[300]
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "neutral"
      },
      style: ({ theme }) => ({
        color: theme.palette.grey[700],
        "&:hover": {
          backgroundColor: theme.palette.grey[200],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[200]
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "success"
      },
      style: ({ theme }) => ({
        color: theme.palette.success.main,
        "&:hover": {
          backgroundColor: theme.palette.success.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.success.light
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "error"
      },
      style: ({ theme }) => ({
        color: theme.palette.error.main,
        "&:hover": {
          backgroundColor: theme.palette.error.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.error.light
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "warning"
      },
      style: ({ theme }) => ({
        color: theme.palette.warning.main,
        "&:hover": {
          backgroundColor: theme.palette.warning.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.warning.light
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "info"
      },
      style: ({ theme }) => ({
        color: theme.palette.info.main,
        "&:hover": {
          backgroundColor: theme.palette.info.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.info.light
          }
        }
      })
    },
    {
      props: {
        variant: "ghost",
        color: "accent"
      },
      style: ({ theme }) => ({
        color: theme.palette.accent.main,
        "&:hover": {
          backgroundColor: theme.palette.accent.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.accent.light
          }
        }
      })
    },

    // VARIANT SURFACE
    {
      props: {
        variant: "surface",
        color: "primary"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.common.black,
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[400]
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "neutral"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.grey[800],
        "&:hover": {
          backgroundColor: theme.palette.grey[300],
          "@media (hover: none)": {
            backgroundColor: theme.palette.grey[300]
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "success"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
        "&:hover": {
          backgroundColor: theme.palette.success.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.success.light
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "error"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.success.light,
        color: theme.palette.error.dark,
        "&:hover": {
          backgroundColor: theme.palette.error.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.error.light
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "warning"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        "&:hover": {
          backgroundColor: theme.palette.warning.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.warning.light
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "info"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
        "&:hover": {
          backgroundColor: theme.palette.info.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.info.light
          }
        }
      })
    },
    {
      props: {
        variant: "surface",
        color: "accent"
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.accent.light,
        color: theme.palette.accent.dark,
        "&:hover": {
          backgroundColor: theme.palette.accent.light,
          "@media (hover: none)": {
            backgroundColor: theme.palette.accent.light
          }
        }
      })
    },

    // SIZES
    {
      props: { size: "small" },
      style: ({ theme }) => ({
        fontSize: 12,
        lineHeight: "18px",
        padding: theme.spacing(0.5, 1.25)
      })
    },
    {
      props: { size: "medium" },
      style: ({ theme }) => ({
        fontSize: 14,
        lineHeight: theme.spacing(2.5),
        padding: theme.spacing(0.75, 2)
      })
    },
    {
      props: { size: "large" },
      style: ({ theme }) => ({
        fontSize: 16,
        lineHeight: theme.spacing(3),
        padding: `${theme.spacing(1)} ${theme.spacing(2.5)}`
      })
    }
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: (theme.shape.borderRadius as number) * 1.5
    })
  }
};

export const MuiFilledInput: {
  defaultProps?: ComponentsProps["MuiFilledInput"];
  styleOverrides?: ComponentsOverrides<Theme>["MuiFilledInput"];
  variants?: ComponentsVariants["MuiFilledInput"];
} = {
  styleOverrides: {
    root: ({ theme }) => {
      return {
        borderTopRightRadius: theme.shape.borderRadius,
        borderTopLeftRadius: theme.shape.borderRadius,
        backgroundColor: "rgba(0, 0, 0, 0.04)"
      };
    }
  }
};
